import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase, debugStorageState } from '../lib/supabase';

const isAuthDebug = () => import.meta.env.DEV || import.meta.env.VITE_DEBUG_AUTH === 'true';
const authLog = (...args) => { if (isAuthDebug()) console.log(...args); };
const authWarn = (...args) => { if (isAuthDebug()) console.warn(...args); };
const authError = (...args) => { if (isAuthDebug()) console.error(...args); };

const AuthContext = createContext(null);

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const debugAuth = import.meta.env.VITE_DEBUG_AUTH === 'true';
  const allowedRoles = useRef(new Set(['admin', 'agent', 'partner', 'student']));
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [authError, setAuthError] = useState(null);
  const fetchingRef = useRef(false);
  const fetchProfilePromiseRef = useRef(null);
  const fetchProfileRef = useRef(null);
  const setProfileWithDebugRef = useRef(null);
  const isInitializingRef = useRef(false);
  const mountedRef = useRef(false);
  const initPromiseRef = useRef(null);
  const initializeAuthRef = useRef(null);
  const profileRef = useRef(null);

  const normalizeRole = useCallback((roleValue) => {
    if (!roleValue) return null;
    const normalized = String(roleValue).trim().toLowerCase();
    return allowedRoles.current.has(normalized) ? normalized : null;
  }, []);

  const handleInvalidRole = useCallback((rawRole, source, userId) => {
    authError('[AuthContext] Invalid or missing role detected.', {
      rawRole,
      roleSource: source,
      userId,
    });
    setAuthError('Unable to load your role. Please try again.');
    return null;
  }, []);

  const handleMissingProfileRole = useCallback(async (userId, source) => {
    authError(`[AuthContext] profile missing role for userId ${userId}`, { source });
    setAuthError('Profile misconfiguration: missing role. Please contact support.');
    setProfile(null);
    setProfileLoading(false);
    try {
      await supabase.auth.signOut();
    } catch (signOutError) {
      authError('[AuthContext] Error signing out after missing role.', signOutError);
    }
    setSession(null);
    setUser(null);
  }, []);

  const isAbortError = useCallback((error) => {
    if (!error) return false;
    if (error.name === 'AbortError') return true;
    return typeof error.message === 'string' && error.message.includes('AbortError');
  }, []);

  // Fetch user profile from the users table using the RPC function
  const fetchProfile = useCallback((userId) => {
    if (fetchingRef.current) {
      if (fetchProfilePromiseRef.current) {
        authWarn('[AuthContext] fetchProfile already in progress, reusing in-flight request.');
        return fetchProfilePromiseRef.current;
      }
      authWarn('[AuthContext] fetchProfile already in progress, but no promise found.');
      return null;
    }

    fetchingRef.current = true;
    const fetchPromise = (async () => {
      try {
        setProfileLoading(true);
        const isRlsRecursionError = (supabaseError) => {
          const message = (supabaseError?.message ?? '').toLowerCase();
          const details = (supabaseError?.details ?? '').toLowerCase();
          const code = supabaseError?.code ?? '';

          return (
            code === '54001'
            || message.includes('infinite recursion')
            || details.includes('infinite recursion')
            || (message.includes('recursion') && message.includes('policy'))
            || message.includes('policy violation')
            || message.includes('row-level security')
            || message.includes('violates row-level security')
          );
        };

        const handleRlsRecursionError = async (supabaseError) => {
          authError('[AuthContext] RLS recursion detected while fetching profile.', supabaseError);
          setSession(null);
          setUser(null);
          setProfile(null);
          try {
            await supabase.auth.signOut();
          } catch (signOutError) {
            authError('[AuthContext] Error signing out after RLS recursion.', signOutError);
          }
          if (window.location.pathname !== '/login') {
            window.location.assign('/login');
          }
        };

        const mapProfileData = (profileData, source) => {
          if (!profileData) {
            return { profile: null, error: 'not-found' };
          }
          if (profileData.role == null) {
            return { profile: null, error: 'missing-role' };
          }
          const normalizedRole = normalizeRole(profileData.role);
          if (!normalizedRole) {
            handleInvalidRole(profileData.role, `mapProfileData:${source}`, userId);
            return { profile: null, error: 'invalid-role' };
          }
          const mappedProfile = {
            id: profileData.id ?? userId ?? null,
            email: profileData.email ?? null,
            role: normalizedRole,
            status: profileData.status ?? null,
            full_name: profileData.full_name ?? null,
            phone: profileData.phone ?? null,
            organization_id: profileData.organization_id ?? null,
            parent_agent_id: profileData.parent_agent_id ?? null,
          };

          // Warn if role is missing - this helps debug profile fetch issues
          return { profile: mappedProfile, error: null };
        };

        const fetchDirectProfile = async () => {
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('id, email, role, status, full_name, phone, organization_id, parent_agent_id')
            .eq('id', userId)
            .single();

          authLog('[AuthContext] Raw Supabase response:', { data: userData, error: userError });

          if (userError) {
            if (isAbortError(userError)) {
              authWarn('[AuthContext] fetchProfile direct query aborted.', userError);
              return { profile: null, error: 'abort' };
            }
            if (isRlsRecursionError(userError)) {
              await handleRlsRecursionError(userError);
            }
            authError('Error fetching user profile:', userError.message);
            return { profile: null, error: 'fetch-failed' };
          }
          if (debugAuth) {
            console.debug('[AuthContext] fetchProfile fallback result', {
              userId,
              role: userData?.role ?? null,
              source: 'direct',
            });
          }
          const mappedProfile = mapProfileData(userData, 'direct');
          if (debugAuth) {
            console.debug('[AuthContext] fetchProfile result', {
              userId: mappedProfile?.profile?.id ?? userId ?? null,
              role: mappedProfile?.profile?.role ?? null,
              source: 'direct',
            });
          }
          return mappedProfile;
        };

        // Use the get_my_profile() RPC function from the database
        const { data, error } = await supabase.rpc('get_my_profile');
        authLog('[AuthContext] Raw Supabase response:', { data, error });

        if (error) {
          if (isAbortError(error)) {
            authWarn('[AuthContext] fetchProfile RPC aborted.', error);
            return { profile: null, error: 'abort' };
          }
          if (isRlsRecursionError(error)) {
            await handleRlsRecursionError(error);
            return { profile: null, error: 'rls-recursion' };
          }
          // Fallback to direct query if RPC function doesn't exist
          if (error.message.includes('function') || error.code === '42883') {
            return fetchDirectProfile();
          }
          authError('Error fetching profile:', error.message);
          return { profile: null, error: 'fetch-failed' };
        }

        const normalizedData = Array.isArray(data) ? data[0] : data;
        if (!normalizedData
          || !Object.prototype.hasOwnProperty.call(normalizedData, 'role')
          || normalizedData.role == null
        ) {
          return fetchDirectProfile();
        }

        if (debugAuth) {
          console.debug('[AuthContext] fetchProfile result', {
            userId,
            role: normalizedData?.role ?? null,
            source: 'rpc',
          });
        }
        return mapProfileData(normalizedData, 'rpc');
      } catch (err) {
        if (isAbortError(err)) {
          authWarn('[AuthContext] fetchProfile aborted.', err);
          return { profile: null, error: 'abort' };
        }
        authError('Error in fetchProfile:', err);
        return { profile: null, error: 'fetch-failed' };
      } finally {
        fetchingRef.current = false;
        fetchProfilePromiseRef.current = null;
        setProfileLoading(false);
      }
    })();

    fetchProfilePromiseRef.current = fetchPromise;
    return fetchPromise;
  }, [debugAuth, handleInvalidRole, isAbortError, normalizeRole]);

  const setProfileWithDebug = useCallback((nextProfile, source) => {
    // Warn if setting profile without role (except when clearing profile)
    if (nextProfile) {
      const normalizedRole = normalizeRole(nextProfile.role);
      if (!normalizedRole) {
        handleInvalidRole(
          nextProfile.role,
          `setProfileWithDebug:${source}`,
          nextProfile?.id ?? user?.id ?? null,
        );
        setProfile(null);
        return;
      }
    }

    const resolvedProfile = nextProfile
      ? { ...nextProfile, role: normalizeRole(nextProfile.role) }
      : null;
    setProfile(resolvedProfile);
    if (debugAuth) {
      console.debug('[AuthContext] profile set', {
        source,
        userId: resolvedProfile?.id ?? user?.id ?? null,
        role: resolvedProfile?.role ?? null,
      });
    }
  }, [debugAuth, handleInvalidRole, normalizeRole, user?.id]);

  useEffect(() => {
    fetchProfileRef.current = fetchProfile;
  }, [fetchProfile]);

  useEffect(() => {
    setProfileWithDebugRef.current = setProfileWithDebug;
  }, [setProfileWithDebug]);

  useEffect(() => {
    profileRef.current = profile;
  }, [profile]);

  const startProfileFetch = useCallback((userId, source) => {
    if (!userId) return null;
    const fetcher = fetchProfileRef.current ?? fetchProfile;
    authLog(`[AuthContext] ${source} - Calling fetchProfile for:`, userId);
    const fetchPromise = fetcher?.(userId);
    if (!fetchPromise) {
      authWarn(`[AuthContext] ${source} - fetchProfile not available, skipping profile update.`);
    }
    return fetchPromise;
  }, [fetchProfile]);

  // Handle session setup (shared between init and events)
  const handleSession = useCallback(async (currentSession, source, profileFetchPromise = null) => {
    if (!mountedRef.current) return;

    if (!currentSession) {
      setSession(null);
      setUser(null);
      setProfile(null);
      setLoading(false);
      return;
    }

    setSession(currentSession);
    setUser(currentSession.user);

    authLog(`[AuthContext] ${source} session user:`, {
      userId: currentSession.user?.id ?? null,
    });

    // Fetch the user's profile (canonical source: public.users via get_my_profile / fetchProfile)
    if (currentSession.user?.id) {
      authLog(`[AuthContext] ${source} - Calling fetchProfile for:`, currentSession.user.id);
      const fetchPromise = fetchProfileRef.current?.(currentSession.user.id);
      if (!fetchPromise) {
        authWarn(`[AuthContext] ${source} - fetchProfile not available, skipping profile update.`);
      } else {
        const profileResult = await fetchPromise;
        authLog(`[AuthContext] ${source} - fetchProfile COMPLETED:`, profileResult);
        if (mountedRef.current) {
          if (profileResult?.profile && profileResult.profile.role) {
            setProfileWithDebugRef.current?.(profileResult.profile, source);
          } else if (profileResult?.error === 'missing-role') {
            await handleMissingProfileRole(currentSession.user.id, source);
          } else if (profileResult?.error === 'abort') {
            console.info(`[AuthContext] ${source} - fetchProfile aborted, leaving profile unchanged.`);
          } else {
            authWarn(`[AuthContext] ${source} - fetchProfile did not return a profile.`, profileResult);
          }
        }
      }
    }

    if (mountedRef.current) {
      setLoading(false);
    }
  }, [handleMissingProfileRole]);

  const getSessionWithLock = useCallback(async (source) => {
    if (initPromiseRef.current) {
      authLog(`[AuthContext] ${source} getSession already in-flight, reusing promise.`);
      return initPromiseRef.current;
    }

    const sessionPromise = (async () => {
      try {
        authLog(`[AuthContext] ${source} calling getSession...`);
        const { data: { session: currentSession }, error } = await supabase.auth.getSession();
        return { session: currentSession, error };
      } finally {
        initPromiseRef.current = null;
      }
    })();

    initPromiseRef.current = sessionPromise;
    return sessionPromise;
  }, []);

  // Initialize auth state
  const initializeAuth = useCallback(() => {
    if (isInitializingRef.current) {
      authLog('[AuthContext] initializeAuth already in progress, skipping duplicate call.');
      return undefined;
    }

    const runInitializeAuth = async () => {
      isInitializingRef.current = true;
      setAuthReady(false);

      try {
        authLog('[AuthContext] initializeAuth START');
        debugStorageState();

        // Safety check: verify supabase client is available
        if (!supabase || !supabase.auth) {
          authError('[AuthContext] CRITICAL: Supabase client not available!', {
            supabase: !!supabase,
            auth: !!supabase?.auth,
          });
          setLoading(false);
          setProfile(null);
          setAuthReady(true);
          return;
        }
        authLog('[AuthContext] Supabase client verified OK');

        const { session: currentSession, error } = await getSessionWithLock('initializeAuth');

        authLog('[AuthContext] getSession COMPLETED:', {
          hasSession: Boolean(currentSession),
          error: error?.message || null,
        });

        if (error) {
          authError('[AuthContext] initializeAuth getSession ERROR:', error);
          if (mountedRef.current) {
            setSession(null);
            setUser(null);
            setProfile(null);
            setLoading(false);
            setAuthReady(true);
          }
          return;
        }

        if (!currentSession) {
          if (mountedRef.current) {
            setSession(null);
            setUser(null);
            setProfile(null);
            setLoading(false);
            setAuthReady(true);
          }
          return;
        }

        const profileFetchPromise = startProfileFetch(currentSession.user?.id, 'initializeAuth');
        await handleSession(currentSession, 'initializeAuth', profileFetchPromise);
      } catch (err) {
        authError('[AuthContext] initializeAuth EXCEPTION:', err);
        if (mountedRef.current) {
          setSession(null);
          setUser(null);
          setProfile(null);
          setLoading(false);
          setAuthReady(true);
        }
      } finally {
        isInitializingRef.current = false;
        if (mountedRef.current) {
          setAuthReady(true);
        }
        authLog('[AuthContext] initializeAuth END');
      }
    };

    runInitializeAuth();

    return undefined;
  }, [getSessionWithLock, handleSession, startProfileFetch]);

  useEffect(() => {
    initializeAuthRef.current = initializeAuth;
  }, [handleSession, initializeAuth, startProfileFetch]);

  useEffect(() => {
    authLog('[AuthContext] useEffect running - calling initializeAuth');
    mountedRef.current = true;
    const initCleanup = initializeAuth();

    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      if (!mountedRef.current) return;

      authLog('[AuthContext] onAuthStateChange:', { event, hasSession: !!newSession });

      if (event === 'SIGNED_IN' && newSession?.user) {
        // During init, only initializeAuth should set session and profile (after getSession()).
        if (isInitializingRef.current) {
          authLog('[AuthContext] SIGNED_IN during init, skipping; initializeAuth will set session and profile.');
          return;
        }
        // Tab visible or same-user re-sign: reuse existing profile to avoid profileLoading and stuck dashboard.
        const currentProfile = profileRef.current;
        if (currentProfile?.id === newSession.user.id && currentProfile?.role) {
          setSession(newSession);
          setUser(newSession.user);
          if (mountedRef.current) {
            setAuthReady(true);
          }
          authLog('[AuthContext] SIGNED_IN - reusing existing profile for user, skipping fetch.');
          return;
        }
        // New login or profile missing: fetch profile and set state.
        setSession(newSession);
        setUser(newSession.user);
        const profileData = await (fetchProfileRef.current?.(newSession.user.id) ?? Promise.resolve(null));
        if (!fetchProfileRef.current) {
          authWarn('[AuthContext] SIGNED_IN - fetchProfile not available, skipping profile update.');
        } else if (mountedRef.current) {
          if (profileData?.profile && profileData.profile.role) {
            setProfileWithDebugRef.current?.(profileData.profile, 'SIGNED_IN');
          } else if (profileData?.error === 'missing-role') {
            await handleMissingProfileRole(newSession.user.id, 'SIGNED_IN');
          } else if (profileData?.error === 'abort') {
            console.info('[AuthContext] SIGNED_IN - fetchProfile aborted, leaving profile unchanged.');
          } else {
            authWarn('[AuthContext] SIGNED_IN - fetchProfile did not return a profile.', profileData);
          }
        }
        if (mountedRef.current) {
          setAuthReady(true);
        }
      } else if (event === 'SIGNED_OUT') {
        setSession(null);
        setUser(null);
        setProfileWithDebugRef.current?.(null, 'SIGNED_OUT');
        if (mountedRef.current) {
          setAuthReady(true);
        }
      }
    });

    // Visibility change handler for tab switching
    // This handles the case where browser throttles JS when tab is inactive
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        authLog('[AuthContext] Tab became visible.');
        // Intentionally do not refresh or mutate session/profile on visibility change.
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      mountedRef.current = false;
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (typeof initCleanup === 'function') {
        initCleanup();
      }
    };
  }, [initializeAuth]);

  // Sign up with email and password
  const signUp = async ({ email, password, fullName, phone, role = 'student', refCode = null }) => {
    try {
      setAuthError(null);

      // Prepare metadata for the user
      const metadata = {
        full_name: fullName,
        phone: phone || null,
        role: role,
      };

      // If there's a referral code, include it
      if (refCode) {
        metadata.ref_code = refCode;
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        setAuthError(error.message);
        return { success: false, error: error.message };
      }

      // Check if email confirmation is required
      if (data.user && !data.session) {
        return {
          success: true,
          message: 'Please check your email to confirm your account.',
          requiresConfirmation: true
        };
      }

      return { success: true, user: data.user, session: data.session };
    } catch (err) {
      const errorMessage = err.message || 'An error occurred during sign up';
      setAuthError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Sign in with email and password
  const signIn = async (email, password) => {
    try {
      setAuthError(null);

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        setAuthError(error.message);
        return { success: false, error: error.message };
      }

      return { success: true, user: data.user, session: data.session };
    } catch (err) {
      const errorMessage = err.message || 'An error occurred during sign in';
      setAuthError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      setAuthError(null);
      const { error } = await supabase.auth.signOut();

      if (error) {
        setAuthError(error.message);
        return { success: false, error: error.message };
      }

      setSession(null);
      setUser(null);
      setProfileWithDebug(null, 'signOut');
      setProfileLoading(false);

      return { success: true };
    } catch (err) {
      const errorMessage = err.message || 'An error occurred during sign out';
      setAuthError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Send password reset email
  const resetPassword = async (email) => {
    try {
      setAuthError(null);

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });

      if (error) {
        setAuthError(error.message);
        return { success: false, error: error.message };
      }

      return { success: true, message: 'Password reset email sent. Please check your inbox.' };
    } catch (err) {
      const errorMessage = err.message || 'An error occurred while sending reset email';
      setAuthError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Update password (used after clicking reset link)
  const updatePassword = async (newPassword) => {
    try {
      setAuthError(null);

      const { data, error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        setAuthError(error.message);
        return { success: false, error: error.message };
      }

      return { success: true, user: data.user };
    } catch (err) {
      const errorMessage = err.message || 'An error occurred while updating password';
      setAuthError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Update user profile
  const updateProfile = async (updates) => {
    try {
      setAuthError(null);

      // Use the update_my_profile() RPC function
      const { data, error } = await supabase.rpc('update_my_profile', {
        updates: updates,
      });

      if (error) {
        // Fallback to direct update if RPC doesn't exist
        if (error.message.includes('function') || error.code === '42883') {
          const { data: updatedData, error: updateError } = await supabase
            .from('users')
            .update(updates)
            .eq('id', user.id)
            .select()
            .single();

          if (updateError) {
            setAuthError(updateError.message);
            return { success: false, error: updateError.message };
          }

          setProfileWithDebug(updatedData, 'updateProfile:direct');
          return { success: true, profile: updatedData };
        }

        setAuthError(error.message);
        return { success: false, error: error.message };
      }

      // Refresh the profile after update
      const refreshedProfileResult = await fetchProfile(user.id);
      if (refreshedProfileResult?.profile) {
        setProfileWithDebug(refreshedProfileResult.profile, 'updateProfile:refresh');
      }

      return { success: true, profile: data || refreshedProfileResult?.profile };
    } catch (err) {
      const errorMessage = err.message || 'An error occurred while updating profile';
      setAuthError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Refresh the user profile
  const refreshProfile = useCallback(async () => {
    if (!user?.id) return null;

    const userProfileResult = await fetchProfile(user.id);
    if (userProfileResult?.profile) {
      setProfileWithDebug(userProfileResult.profile, 'refreshProfile');
    }
    return userProfileResult?.profile ?? null;
  }, [user?.id, fetchProfile, setProfileWithDebug]);

  const role = profile?.role ?? null;
  const displayName = profile?.full_name ?? user?.user_metadata?.full_name ?? user?.email ?? 'User';

  // Check if user has a specific role
  const hasRole = useCallback((roles) => {
    if (!role) return false;
    if (typeof roles === 'string') {
      return role === roles;
    }
    return roles.includes(role);
  }, [role]);

  // Role check helpers
  const isAdmin = useCallback(() => hasRole('admin'), [hasRole]);
  const isAgent = useCallback(() => hasRole(['agent', 'admin']), [hasRole]);
  const isPartner = useCallback(() => hasRole('partner'), [hasRole]);
  const isStudent = useCallback(() => hasRole('student'), [hasRole]);

  // Clear any auth errors
  const clearError = useCallback(() => {
    setAuthError(null);
  }, []);

  const value = {
    // State
    session,
    user,
    profile,
    role,
    displayName,
    loading,
    profileLoading,
    authReady,
    error: authError,
    isAuthenticated: !!session,

    // Auth methods
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,

    // Profile methods
    updateProfile,
    refreshProfile,

    // Role helpers
    hasRole,
    isAdmin,
    isAgent,
    isPartner,
    isStudent,

    // Utilities
    clearError,

    // Legacy aliases for backwards compatibility
    login: signIn,
    logout: signOut,
    register: signUp,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
